import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import notificationConfigTypes from 'app/utils/static/notificationConfigTypes';

import HeaderThin from 'app/components/layout/HeaderThin';
import SectionGroup from 'app/components/general/SectionGroup';
import SchemaForm from 'app/components/general/SchemaForm';

import * as notificationConfigActions from 'app/ducks/notificationConfigs/actions';
import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as notificationConfigSelectors from 'app/ducks/notificationConfigs/selectors';
import * as organisationSelectors from 'app/ducks/organisations/selectors';
import * as competitionSelectors from 'app/ducks/competitions/selectors';

const formSchema = {
  required: ['name', 'notificationType'],
  properties: {
    notificationType: {
      type: 'object',
      title: 'Type',
      options: notificationConfigTypes,
      inputType: 'select',
    },
    name: {
      type: 'string',
      title: 'Name',
    },
    email: {
      type: 'string',
      title: 'Email',
    },
    mobile: {
      type: 'string',
      title: 'Mobile',
    },
    dayConfig: {
      type: 'array',
      title: 'Section Name Filters (Optional)',
      inputType: 'select',
      createable: true,
      isMulti: true,
    },
  },
};

const uiSchema = {
  notificationType: { 'ui:field': 'select', classNames: 'col' },
  dayConfig: { 'ui:field': 'select', classNames: 'col' },
};

interface INotificationConfigCreateProps {
  appReady: boolean;
  actions: any;
  match: any;
  notificationConfigs: any;
  organisations: any;
  competitions: any;
  errors: any;
}

interface INotificationConfigCreateState {
  originalFormData: any;
  formData: any;
  changed: boolean;
  saved: boolean;
  errors: any;
}

const selector = (state): any => ({
  notificationConfigs: notificationConfigSelectors.getNotificationConfigs(
    state,
  ),
  organisations: organisationSelectors.getOrganisations(state),
  competitions: competitionSelectors.getCompetitions(state),
  appReady: loadingSelectors.appReady(state),
  errors: {
    ...state.notificationConfigs.backendErrors,
  },
});

const actionCreators = _.merge(notificationConfigActions);

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class NotificationConfigCreate extends React.Component<
  INotificationConfigCreateProps,
  INotificationConfigCreateState
> {
  static defaultProps: INotificationConfigCreateProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    notificationConfigs: undefined,
    organisations: undefined,
    competitions: undefined,
    errors: undefined,
  };

  state: INotificationConfigCreateState = {
    originalFormData: {},
    formData: {
      name: undefined,
      email: undefined,
      notificationType: undefined,
      mobile: undefined,
    },
    changed: false,
    saved: false,
    errors: {},
  };

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed) {
      return {
        ...prevState,
        errors: nextProps.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleSubmit = (e): void => {
    if (this.isValid()) {
      const payload = {
        name: this.state.formData.name,
        email: this.state.formData.email,
        mobile: this.state.formData.mobile,
        dayConfig: !_.isEmpty(this.state.formData.dayConfig) ? _.map(this.state.formData.dayConfig, (m) => m.value).join('|') : null,
        notificationType: !_.isEmpty(this.state.formData.notificationType)
          ? this.state.formData.notificationType.id
          : null,
        competition: {
          id: this.props.competitions.currentCompetitionId,
        },
        organisation: {
          id: this.props.organisations.currentOrganisationId,
        },
      };

      this.props.actions.createNotificationConfig(payload);
    }
    this.setState({ changed: false });
  };

  render(): React.ReactNode {
    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin
              title="Create Notification Contact"
              divider
              subtitle={this.props.competitions.currentCompetition.name}
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-xl-6">
            <SectionGroup title="Notification Contact Details">
              <div className="row">
                <div className="col">
                  <span className="form-description">
                    This person will be contacted via email/SMS in the event of a forfeit by a team.
                  </span>
                </div>
              </div>
              <div className="row with-margin-top">
                <div className="col">
                  <SchemaForm
                    uiSchema={uiSchema}
                    schema={formSchema}
                    handleChange={this.handleChange}
                    handleError={this.handleError}
                    handleSubmit={this.handleSubmit}
                    formData={this.state.formData}
                    errors={this.props.errors}
                    liveValidate={false}
                    isValid={this.isValid()}
                    isLoading={this.props.notificationConfigs.isSaving}
                    hasSaved={this.props.notificationConfigs.hasSaved}
                    hasFailed={this.props.notificationConfigs.hasFailed}
                    originalFormData={this.state.originalFormData}
                    buttonText="Create Contact"
                  />
                </div>
              </div>
            </SectionGroup>
          </div>
        </div>
      </div>
    );
  }
}
